import { type Reimbursement } from '@kalos/kalos-rpc';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Skeleton,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useConfirm,
} from '@kalos/ui';
import {
  CheckCircledIcon,
  CrossCircledIcon,
  Pencil1Icon,
  PlusIcon,
  ReloadIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { Images } from 'lucide-react';
import { Fragment, useCallback, useMemo, useState } from 'react';

import { useAuth } from '../../../context/AuthContext';
import {
  useReimbursementDeleteMutation,
  useReimbursementDocumentBatchGetQuery,
  useReimbursementUpdateMutation,
} from '../../../hooks/react-query/useReimbursementQuery';
import { getMimeType } from '../../../tools/helpers';
import { ImageGallery } from '../../ComponentsLibrary/OmniGallery/components/ImageGallery';
import {
  buildReimbursementApproveRequest,
  buildReimbursementDeleteRequest,
  buildReimbursementEditRequest,
  buildReimbursementProcessRequest,
  buildReimbursementRejectRequest,
  buildReimbursementSubmitRequest,
  getReimbursementActions,
  isReimbursementAlreadyProcessed,
  REIMBURSEMENT_ACTION_COPY,
  type ReimbursementActionKey,
} from './reimbursementActions';
import { ReimbursementEditForm } from './ReimbursementEditForm';

const EditAction = ({ reimbursement: r }: { reimbursement: Reimbursement }) => {
  const updateReimbursementMutation = useReimbursementUpdateMutation();
  const defaultEditFormValues = useMemo<
    React.ComponentProps<typeof ReimbursementEditForm>['defaultValues']
  >(
    () => ({
      amount: r.amount,
      description: r.description,
      jobNumber: r.jobNumber,
      userId: r.userId,
    }),
    [r.amount, r.description, r.jobNumber, r.userId],
  );
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const handleUpdateReimbursement = useCallback<
    React.ComponentProps<typeof ReimbursementEditForm>['onSave']
  >(
    async ({ data, dirtyFields }) => {
      try {
        await updateReimbursementMutation.mutateAsync(
          buildReimbursementEditRequest(r.id, data, dirtyFields),
        );
        toast({
          variant: 'success',
          title: REIMBURSEMENT_ACTION_COPY.edit.success,
        });
        setIsEditDialogOpen(false);
      } catch {
        toast({
          variant: 'destructive',
          title: REIMBURSEMENT_ACTION_COPY.edit.error,
        });
      }
    },
    [r, updateReimbursementMutation],
  );

  return (
    <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
      <Tooltip key="editAll">
        <TooltipContent>Edit Reimbursement</TooltipContent>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              isLoading={updateReimbursementMutation.isPending}
              disabled={updateReimbursementMutation.isPending}
              size="icon"
              variant="outline"
            >
              <Pencil1Icon />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
      </Tooltip>

      <DialogContent className="@container">
        <DialogTitle>Edit Reimbursement</DialogTitle>
        <ReimbursementEditForm
          onSave={handleUpdateReimbursement}
          disableTechnicianPicker
          isLoading={updateReimbursementMutation.isPending}
          disabled={updateReimbursementMutation.isPending}
          defaultValues={defaultEditFormValues}
        />
      </DialogContent>
    </Dialog>
  );
};

const ProcessAction = ({ reimbursement: r }: { reimbursement: Reimbursement }) => {
  const updateReimbursementMutation = useReimbursementUpdateMutation();
  const confirm = useConfirm();
  const handlePayroll = async () => {
    if (!(await confirm(REIMBURSEMENT_ACTION_COPY.process.confirm))) return;
    try {
      await updateReimbursementMutation.mutateAsync(buildReimbursementProcessRequest(r));
      toast({
        variant: 'success',
        title: REIMBURSEMENT_ACTION_COPY.process.success,
      });
    } catch {
      toast({
        variant: 'destructive',
        title: REIMBURSEMENT_ACTION_COPY.process.error,
      });
    }
  };

  return (
    <Tooltip>
      <TooltipContent className="max-w-72">
        <div>Process Reimbursement</div>
        {r.payrollProcessed && (
          <div className="text-primary text-xs">(disabled because already processed)</div>
        )}
      </TooltipContent>
      <TooltipTrigger asChild>
        <span>
          <Button
            size="icon"
            variant="outline"
            onClick={handlePayroll}
            isLoading={updateReimbursementMutation.isPending}
            disabled={isReimbursementAlreadyProcessed(r) || updateReimbursementMutation.isPending}
          >
            <ReloadIcon />
          </Button>
        </span>
      </TooltipTrigger>
    </Tooltip>
  );
};

const ApproveAction = ({ reimbursement: r }: { reimbursement: Reimbursement }) => {
  const updateReimbursementMutation = useReimbursementUpdateMutation();
  const confirm = useConfirm();
  const loggedUserId = useAuth().user.id;

  const handleApprove = async () => {
    if (!(await confirm(REIMBURSEMENT_ACTION_COPY.approve.confirm))) return;
    try {
      await updateReimbursementMutation.mutateAsync(
        buildReimbursementApproveRequest(r, loggedUserId),
      );
      toast({
        variant: 'success',
        title: REIMBURSEMENT_ACTION_COPY.approve.success,
      });
    } catch {
      toast({
        variant: 'destructive',
        title: REIMBURSEMENT_ACTION_COPY.approve.error,
      });
    }
  };

  return (
    <Tooltip>
      <TooltipContent>Approve Reimbursement</TooltipContent>
      <TooltipTrigger asChild>
        <Button
          size="icon"
          variant="outline"
          onClick={handleApprove}
          isLoading={updateReimbursementMutation.isPending}
          disabled={updateReimbursementMutation.isPending}
        >
          <CheckCircledIcon />
        </Button>
      </TooltipTrigger>
    </Tooltip>
  );
};

const DeleteAction = ({ reimbursement: r }: { reimbursement: Reimbursement }) => {
  const deleteReimbursementMutation = useReimbursementDeleteMutation();
  const confirm = useConfirm();

  const handleDelete = async () => {
    if (!(await confirm(REIMBURSEMENT_ACTION_COPY.delete.confirm))) return;
    try {
      await deleteReimbursementMutation.mutateAsync(buildReimbursementDeleteRequest(r));
      toast({
        variant: 'success',
        title: REIMBURSEMENT_ACTION_COPY.delete.success,
      });
    } catch {
      toast({
        variant: 'destructive',
        title: REIMBURSEMENT_ACTION_COPY.delete.error,
      });
    }
  };

  return (
    <Tooltip>
      <TooltipContent>Delete Reimbursement</TooltipContent>
      <TooltipTrigger asChild>
        <Button
          size="icon"
          variant="outline"
          onClick={handleDelete}
          isLoading={deleteReimbursementMutation.isPending}
          disabled={deleteReimbursementMutation.isPending}
        >
          <TrashIcon />
        </Button>
      </TooltipTrigger>
    </Tooltip>
  );
};

const RejectAction = ({ reimbursement: r }: { reimbursement: Reimbursement }) => {
  const updateReimbursementMutation = useReimbursementUpdateMutation();
  const confirm = useConfirm();

  const handleReject = async () => {
    if (!(await confirm(REIMBURSEMENT_ACTION_COPY.reject.confirm))) return;
    try {
      await updateReimbursementMutation.mutateAsync(buildReimbursementRejectRequest(r));
      toast({
        variant: 'success',
        title: REIMBURSEMENT_ACTION_COPY.reject.success,
      });
    } catch {
      toast({
        variant: 'destructive',
        title: REIMBURSEMENT_ACTION_COPY.reject.error,
      });
    }
  };

  return (
    <Tooltip>
      <TooltipContent>Reject Reimbursement</TooltipContent>
      <TooltipTrigger asChild>
        <Button
          size="icon"
          variant="outline"
          onClick={handleReject}
          isLoading={updateReimbursementMutation.isPending}
          disabled={updateReimbursementMutation.isPending}
        >
          <CrossCircledIcon />
        </Button>
      </TooltipTrigger>
    </Tooltip>
  );
};

const SubmitAction = ({ reimbursement: r }: { reimbursement: Reimbursement }) => {
  const updateReimbursementMutation = useReimbursementUpdateMutation();
  const confirm = useConfirm();

  const handleSubmit = async () => {
    if (!(await confirm(REIMBURSEMENT_ACTION_COPY.submit.confirm))) return;
    try {
      await updateReimbursementMutation.mutateAsync(buildReimbursementSubmitRequest(r));
      toast({
        variant: 'success',
        title: REIMBURSEMENT_ACTION_COPY.submit.success,
      });
    } catch {
      toast({
        variant: 'destructive',
        title: REIMBURSEMENT_ACTION_COPY.submit.error,
      });
    }
  };

  return (
    <Tooltip>
      <TooltipContent>Submit Reimbursement</TooltipContent>
      <TooltipTrigger asChild>
        <Button
          size="icon"
          variant="outline"
          onClick={handleSubmit}
          isLoading={updateReimbursementMutation.isPending}
          disabled={updateReimbursementMutation.isPending}
        >
          <PlusIcon />
        </Button>
      </TooltipTrigger>
    </Tooltip>
  );
};

const ReimbursementGallery = ({
  reimbursementId,
  trigger,
}: {
  reimbursementId: Reimbursement['id'];
  trigger: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const reimbursementDocumentsQuery = useReimbursementDocumentBatchGetQuery({
    filter: {
      reimbursementId,
    },
    enabled: isOpen,
    select(data) {
      return data.results.map((doc) => ({
        id: doc.fileId,
        name: doc.description,
        bucket: 'kalos-reimbursements',
        mimeType: getMimeType(doc.description) || '.png',
      }));
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-h-screen max-w-screen-2xl overflow-auto px-0 pb-0 pt-0 md:max-h-[90vh]">
        {reimbursementDocumentsQuery.isPending && <Skeleton className="h-[80vh] w-full" />}
        {reimbursementDocumentsQuery.isSuccess && (
          <div className="h-[80vh]">
            <ImageGallery title="Reimbursement Uploads" files={reimbursementDocumentsQuery.data} />{' '}
          </div>
        )}
      </DialogContent>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
    </Dialog>
  );
};

export const ReimbursementTableActions = ({
  reimbursement: r,
  isManager,
  isOwner,
  isPayroll,
}: {
  reimbursement: Reimbursement;
  isOwner: boolean;
  isPayroll: boolean;
  isManager: boolean;
}) => {
  const editAction = <EditAction reimbursement={r} />;

  const galleryAction = (
    <Tooltip>
      <ReimbursementGallery
        reimbursementId={r.id}
        trigger={
          <TooltipTrigger asChild>
            <Button size="icon" variant="outline">
              <Images size={15} />
            </Button>
          </TooltipTrigger>
        }
      />
      <TooltipContent>View Reimbursement Photos</TooltipContent>
    </Tooltip>
  );

  const processAction = <ProcessAction reimbursement={r} />;

  const approveAction = <ApproveAction reimbursement={r} />;

  const deleteAction = <DeleteAction reimbursement={r} />;

  const rejectAction = <RejectAction reimbursement={r} />;

  const submitAction = <SubmitAction reimbursement={r} />;

  const actionElements: Record<ReimbursementActionKey, React.ReactNode> = {
    gallery: galleryAction,
    edit: editAction,
    delete: deleteAction,
    submit: submitAction,
    approve: approveAction,
    reject: rejectAction,
    process: processAction,
  };

  // Which actions show, and in what order, is shared with the phone record.
  const actions = getReimbursementActions({ statusId: r.statusId, isOwner, isManager, isPayroll });

  return (
    <TooltipProvider>
      {actions.map((key) => (
        <Fragment key={key}>{actionElements[key]}</Fragment>
      ))}
    </TooltipProvider>
  );
};
